/** Pure template-string renderers for group-admin and user-class surfaces.
 *
 * Layer: `view` per docs/design/spa-account-extraction-260524.md §3.2.
 *
 * Covers:
 *   - renderGroupAdminCta   — group-admin entry-point section
 *   - re-exports renderUserClassBadge from view-usage.ts for callers that only
 *     need the badge without importing from view-usage directly
 *
 * Purity contract: every exported function accepts typed data and returns a
 * string. No DOM access, no fetch, no window.X mutations.
 *
 * Extracted from web/static/account.html in Phase AC.4.
 */
export function renderGroupAdminCta(esc, data) {
    const groups = Array.isArray(data.groups) ? data.groups : [];
    const adminGroups = groups.filter(g => g != null && g.type === 'institutional'
        && (g.is_root_admin || g.has_change_member_quotas || g.has_buy_tokens));
    if (adminGroups.length === 0)
        return '';
    const primary = adminGroups[0]; // length guard above guarantees non-undefined
    const cards = adminGroups.map(g => {
        const status = g.status === 'expiring'
            ? `<span class="tier-badge" style="background:#fef3c7;color:#b45309;margin-left:6px">Expiring</span>`
            : `<span class="tier-badge institutional" style="margin-left:6px">Active</span>`;
        return `<div style="border:1px solid var(--border);border-radius:6px;padding:12px 14px;margin-bottom:10px;font-size:13px;background:var(--surface)">
            <div style="font-weight:600;margin-bottom:4px;font-size:14px">
                ${esc(g.name || g.id || 'Untitled group')} ${status}
            </div>
            <div style="font-size:12px;color:var(--text-muted)">
                ${esc(g.member_count || 0)} member${g.member_count === 1 ? '' : 's'}
                ${g.unlimited_mode === 'invoiced' ? '· Invoiced billing' : (g.unlimited_mode === 'flat' ? '· Flat billing' : '')}
            </div>
            <div style="display:flex;gap:8px;margin-top:8px;padding-top:8px;border-top:1px solid var(--border)">
                <a href="/admin?group=${esc(g.id || '')}" class="account-btn primary" style="font-size:12px;padding:4px 10px;margin:0">Admin pane</a>
            </div>
        </div>`;
    }).join('');
    return `<section class="account-section" data-testid="group-admin-cta" aria-labelledby="group-admin-h">
        <h2 id="group-admin-h">Group Administration</h2>
        <div class="group-admin-cta" role="region" aria-label="Group admin entry point">
            <div class="group-admin-cta-icon" aria-hidden="true">&#128736;</div>
            <div class="group-admin-cta-text">
                <h3>Manage your group</h3>
                <p>You are the admin of <strong>${esc(primary.name || 'your group')}</strong>.
                   Invite members, set per-user caps, view usage, and generate invoices.</p>
            </div>
            <a href="/admin" class="account-btn primary" style="margin-top:0;white-space:nowrap;margin-left:auto"
               aria-label="Go to admin pane">Manage group &rarr;</a>
        </div>
        <div style="margin-top:14px;padding-top:14px;border-top:1px solid var(--border)">
            <h3 style="font-size:13px;font-weight:700;color:var(--text-muted);margin-bottom:8px;text-transform:uppercase;letter-spacing:0.5px">
                All groups you admin
            </h3>
            ${cards}
        </div>
    </section>`;
}
